import * as React from 'react';
import { clsx } from 'clsx';
import { ApiError, apiPost, logApiError } from "../api";
import { useContext, useState, useEffect } from 'react';
import DialogStyled from "../components/dialogStyled";
import Dump from "../components/dump";
import Spinner, { SpinnerColorsEnum } from "../components/spinner";
import { isFunction } from "../helpers/utils";
import { fetchContact, resendCode, stepBack } from "../loaders";
import ColumnMain from "../src/columnMain";
import ColumnSide from "../src/columnSide";
import Layout from "../src/layout";
import SummaryExtended from "../src/summaryExtended";
import { ContextSession } from "./app";
import { useForm, type SubmitHandler } from 'react-hook-form';
import HookFormDevToolLazy from "../../js/components/hookFormDevToolLazy";
import FormCode from "./formCode";
import { ACCOUNT_NAME_MAX_LENGTH, inputClass, inputRadioClass, suggestAccountName } from "./utils";
import { ErrorMessage } from "@hookform/error-message";
import FieldError from "../components/fieldError";
import LockedNotice from "../components/lockedNotice";
import { DiscountBanner, useAvailableDiscount } from "../components/discount";

type Props = Readonly<{
	setState: Function,
	offer?: {},
	memberLoginUrl?: string | null,
}>

type DebitFormValues = {
	holder: string,
	// iban: string,
	accountNumber: string,
	sortCode: string,
	consentDebit: boolean,
}

function DirectDebit({setState, offer, memberLoginUrl, ...otherProps}: Props) {

	const state = useContext(ContextSession);

	// The direct-debit step is subscription-only, so the DD discount always applies here.
	const discount = useAvailableDiscount(state.hash, true);
	const [contact, setContact] = useState(null);

	// SMS resend feedback: 'idle' | 'sending' | 'sent'.
	const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent'>('idle');

	const handleResend = () => {
		setResendState('sending');
		resendCode(state.hash, setState)
			.then(() => setResendState('sent'))
			.catch(() => setResendState('idle'));
	};

	// Past the point of no return: the mandate exists. Resubmitting the form would
	// PUT the mandate and fire a fresh signature request against one already signed,
	// so the form is shown read-only, the same as the details and start-date steps.
	const committed = Boolean(state?.mandateId || state?.payableId);

	const {
		register,
		handleSubmit,
		formState: { errors, isSubmitting },
		control,
		setError,
		reset,
	} = useForm<DebitFormValues>({
		defaultValues: {
			// Prefilled only in development, from .env.development. The sandbox gateway
			// accepts these test bank details; production leaves both fields empty.
			accountNumber: process.env.PUBLIC_DEBIT_TEST_ACCOUNT_NUMBER || '',
			sortCode: process.env.PUBLIC_DEBIT_TEST_SORT_CODE || '',
			consentDebit: false,
		}
	});

	const onSubmit: SubmitHandler<DebitFormValues> = (data) => {
		data.contactId = contact['@id'];
		data.contactNumber = contact['number'];
		data.contactFamilyName = contact['familyName'];
		data.contactGivenName = contact['givenName'];

		let address = {...contact['address']};
		delete address['@id'];
		delete address['@type'];
		delete address['supplement'];

		data.contactAddress = address;

		// console.log(data);

		return apiPost('/resa/directDebit/' + state?.hash, data).then((response) => {
			if (isFunction(setState)) {
				setState(response.data);
			}
		}).catch(function (error) {
			if (error instanceof ApiError) {
				// The server translates the bank's field-level rejections, so point the
				// customer at the field that is actually wrong rather than telling them
				// to check everything.
				const fieldErrors = error.data?.fieldErrors;

				if (fieldErrors && Object.keys(fieldErrors).length > 0) {
					Object.entries(fieldErrors).forEach(([field, message]) => {
						setError(field as keyof DebitFormValues, { type: 'server', message: String(message) });
					});
				} else {
					setError("root.serverError", {
						type: String(error.status),
						message: 'Error has occurred. Check your entered data and try again.'
					});
				}

				logApiError(error);
			}
		});
	};

	useEffect(() => {
		fetchContact(state?.hash).then((data) => {
			setContact(data);
			// Suggested, not imposed — see suggestAccountName. The member keeps their
			// full name on their contact record; this is only the name on the bank
			// account, which BACS limits to 18 characters.
			reset({holder: suggestAccountName(data?.givenName, data?.familyName)})
		});
	}, []);

	function closeModal() {
		stepBack(state.hash, setState);
	}

	return (<>
		<Layout name={"DIRECT DEBIT"}>
			<ColumnMain className="order-2 lg:order-1" banner={discount ? <DiscountBanner discount={discount}/> : null}>
				<div className="relative">
				{/* Once committed the whole form is inert and the overlay below fades it. */}
				<form onSubmit={handleSubmit(onSubmit)} inert={committed || undefined}>
					<HookFormDevToolLazy control={control}/>

					<h3>COMPLETE YOUR DIRECT DEBIT MANDATE</h3>
					<h5 className="mb-2">DIRECT DEBIT DETAILS</h5>
					<p className="font-bold">Company: Harlands Re Resamania SUN: 174168</p>

					<ErrorMessage errors={errors} name={'root.serverError'} />

					<input
						type="text"
						placeholder="Account name *"
						maxLength={ACCOUNT_NAME_MAX_LENGTH}
						className={clsx('w-full mb-1', inputClass(errors.holder))}
						{...register("holder", {
							required: true,
							maxLength: ACCOUNT_NAME_MAX_LENGTH,
						})}
					/>
					<FieldError error={errors.holder} message="Enter the account holder name"/>
					<p className="text-grey-600 text-xs mb-5">
						The name on your bank account, as your bank holds it. Your bank allows
						up to {ACCOUNT_NAME_MAX_LENGTH} characters here, so you may need to
						shorten it — for example <span className="whitespace-nowrap">C Fairweather</span>.
						This does not change the name on your membership.
					</p>

					<input
						type="text"
						placeholder="Sort code *"
						className={clsx('w-full', inputClass(errors.sortCode))}
						{...register("sortCode", {required: true})}
					/>
					<FieldError error={errors.sortCode} message="Enter your sort code"/>

					<input
						type="text"
						placeholder="Account number *"
						className={clsx('w-full', inputClass(errors.accountNumber))}
						{...register("accountNumber", {required: true})}
					/>
					<FieldError error={errors.accountNumber} message="Enter your account number"/>

					<div className={clsx('border px-2 -mx-2 flex py-1 gap-x-3 items-start mb-6', inputRadioClass(errors.consentDebit))}>
						<input
							className="mt-0.5 mr-0 !mb-0 !focus:outline-0"
							id="consentDebit"
							type="checkbox"
							//checked
							{...register("consentDebit", {required: true})}
						/>
						<label className="mb-0 text-xs inline-block align-middle" htmlFor="consentDebit">
							I confirm that I am the account holder to be debited and that I am the only person required to authorise debits
						</label>
					</div>

					<p className="text-red-500 text-sm">
						In order to finalise your mandate creation, you must confirm that you are the account holder to be debited
						and the only person required to authorise debits
					</p>

					<hr/>

					<img src="/images/directdebit.png" alt="" width={163} height={53} className="mb-8"/>
					<h5>DIRECT DEBIT GUARANTEE</h5>
					<p className="text-xs">
						This guarantee is offered by all banks and building societies that accept direct debit payment instructions.
					</p>
					<p className="text-xs">
						In case of change in the amount, date or frequency of your direct debit, Harlands Re Resamania will notify you 10
						working days before your account debited or otherwise agreed. If you ask Harlands Re Resamania to collect a payment,
						a confirmation of the amount and date will be given at the time of the request.
					</p>
					<p className="text-xs">
						If an error is made in the payment of your direct debit, by Harlands Re Resamania or your bank or building society,
						you are entitled to a full and immediate refund of the amount paid by your bank or building society - If you receive
						a refund to which you do not have right, you need to refund when Harlands Re Resamania asks you to do so.
					</p>
					<p className="text-xs">
						You can cancel a direct debit at any time by simply contacting your bank or building society. Written confirmation
						may be required. Please also inform us.
					</p>

					<hr className="my-8"/>

					<div className="text-red-400 mb-8">
						<ErrorMessage errors={errors} name={'root.serverError'}/>
					</div>

					<div className="flex flex-wrap gap-x-4 sm:gap-x-8 gap-y-4 justify-center">
						{/*
							Always available. Before the mandate exists this returns to the
							editable details form; once it exists (committed) the details form
							renders read-only, so back is safe — it lands on the locked review
							with its restart / portal options rather than an editable form that
							would break on re-submit.
						*/}
						<button className="grey !mb-0" onClick={(event) => {
							event.preventDefault();
							stepBack(state.hash, setState);
						}}>BACK</button>
						<button type="submit" className="mb-0 flex items-center justify-center" disabled={isSubmitting}>
							SUBMIT & VERIFY
							{isSubmitting && <Spinner color={SpinnerColorsEnum.Black} className="!w-4 !h-4"/>}
						</button>
					</div>
				</form>
				{committed && <LockedNotice hash={state.hash} setState={setState} memberLoginUrl={memberLoginUrl}/>}
				</div>
			</ColumnMain>
			<ColumnSide className="order-1 lg:order-2">
				<SummaryExtended offer={offer} startDate={state?.startDate ? new Date(state.startDate) : new Date()} discount={discount}/>
			</ColumnSide>
		</Layout>

		<DialogStyled
			isOpen={state?.step === 5}
			onClose={closeModal}
			dismissable={false}
			classesSize={"max-w-[90vw] lg:max-w-96 max-h-[97vh] rounded-xl text-black text-center"}
		>
			<div className="p-6 sm:p-8">
				<h3>SMS AUTHORISATION</h3>
				<p>We’ve sent you an authorisation code to your mobile phone number.</p>
				<p className="mb-0">Please enter the code below. This will verify and sign your Direct Debit mandate.</p>
			</div>

			<FormCode setState={setState}/>

			{/* Ghost button: resend the code for a customer who didn't receive it. */}
			<div className="px-6 pb-6 -mt-2 text-center">
				{resendState === 'sent' ?
					<p className="text-xs text-grey-600 mb-0">A new code has been sent.</p>
					:
					<button
						type="button"
						disabled={resendState === 'sending'}
						onClick={handleResend}
						className="!border-0 !bg-transparent !p-0 !m-0 !min-h-0 !text-inherit !normal-case !font-normal underline text-sm cursor-pointer disabled:opacity-50 disabled:cursor-default"
					>
						{resendState === 'sending' ? 'Sending…' : 'Didn’t get a code? Resend it'}
					</button>
				}
			</div>
		</DialogStyled>

		{/*<Dump value={contact}/>*/}
	</>);
}

export default DirectDebit;
